import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { getArticleById, getRelatedArticles, getCategoryBySlug } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const article = getArticleById(id || '');

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="flex flex-col items-center justify-center h-screen text-center px-4 pt-16">
          <i className="ri-file-unknow-line text-5xl text-foreground-400 mb-4"></i>
          <h1 className="text-2xl font-bold text-foreground-900">文章不存在</h1>
          <Link to="/" className="mt-4 text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const category = getCategoryBySlug(article.categorySlug);
  const relatedArticles = getRelatedArticles(article.id, article.categorySlug);

  const articleSchema = {
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: article.publishedAt,
    author: { '@type': 'Person', name: article.author },
    publisher: { '@type': 'Organization', name: '黑料吃瓜在线' },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `http://hngzxny.cn/article/${article.id}` },
  };

  const breadcrumbSchema = {
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://hngzxny.cn/' },
      { '@type': 'ListItem', position: 2, name: category?.name || '', item: `http://hngzxny.cn/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <SchemaOrg type="Article" data={articleSchema} />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />

      <main>
        <Navbar />

        <article className="pt-24 pb-14 md:pb-20 px-4 md:px-6">
          <div className="max-w-[800px] mx-auto">
            <Breadcrumb
              items={[
                { label: category?.name || '', href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />

            <header className="mt-8">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-900 whitespace-nowrap">
                  {article.category}
                </span>
                {article.isHot && (
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-accent-500 text-background-50 whitespace-nowrap">
                    热门
                  </span>
                )}
                <span className="text-sm text-foreground-500 whitespace-nowrap">{article.publishedAt}</span>
                <span className="text-sm text-foreground-500 whitespace-nowrap">
                  <i className="ri-eye-line mr-1"></i>
                  {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 阅读
                </span>
              </div>

              <h1 className="mt-4 font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight text-balance">
                {article.title}
              </h1>

              <div className="flex items-center gap-3 mt-5 text-sm text-foreground-500">
                <span className="whitespace-nowrap">作者：{article.author}</span>
                <span className="whitespace-nowrap">来源：{article.source}</span>
                <span className="whitespace-nowrap">{article.readTime} {t('minute_read')}</span>
              </div>
            </header>

            <div className="mt-8 rounded-lg overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={article.title}
                className="w-full h-auto object-cover object-top"
              />
            </div>

            <div
              className="mt-10 prose prose-base max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 flex items-center gap-2 flex-wrap">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-900 whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-20 bg-background-100 px-4 md:px-6">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex items-end justify-between mb-10">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                    {t('related')}
                  </h2>
                  <p className="mt-2 text-sm text-foreground-500">你可能也感兴趣的内容</p>
                </div>
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="hidden sm:inline-flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('view_all')} <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              <div className="relative">
                <div className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide" style={{ scrollSnapType: 'x mandatory' }}>
                  {relatedArticles.map((ra) => (
                    <div key={ra.id} style={{ scrollSnapAlign: 'start' }}>
                      <ArticleCard article={ra} variant="compact" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        <Footer />
      </main>
    </>
  );
}